import { useEffect } from 'react';
import { useWeb3React } from '@web3-react/core';
import { useWallet } from '../contexts/WalletContext';
import { useBalance } from './useBalance';

/**
 * Hook to synchronize Web3React state to WalletContext
 */
export const useWeb3Sync = () => {
  const { account, chainId, isActive } = useWeb3React();
  const { formattedBalance } = useBalance();
  const { account: walletAccount, chainId: walletChainId, balance: walletBalance, updateAccount, updateChainId, updateBalance } = useWallet();

  // Sync account status - only update when the value actually changes
  useEffect(() => {
    const newAccount = account || null;
    if (newAccount !== walletAccount) {
      console.log('🔄 Account status changed:', { from: walletAccount, to: newAccount });
      updateAccount(newAccount);
    }
  }, [account, walletAccount, updateAccount]);

  // Sync chain ID - only update when the value actually changes
  useEffect(() => {
    const newChainId = chainId || null;
    if (newChainId !== walletChainId) {
      console.log('🔄 Chain ID status changed:', { from: walletChainId, to: newChainId });
      updateChainId(newChainId);
    }
  }, [chainId, walletChainId, updateChainId]);

  // Sync balance - only update when the value actually changes (add threshold to avoid loops from minor differences)
  useEffect(() => {
    const newBalance = formattedBalance || '0';
    const currentBalance = walletBalance || '0';
    
    // If the strings are identical, skip directly
    if (newBalance === currentBalance) {
      return;
    }
    
    // Calculate the numerical difference, if the difference is less than 0.0001, it is considered unchanged
    const newBalanceNum = parseFloat(newBalance);
    const currentBalanceNum = parseFloat(currentBalance);
    const threshold = 0.0001;
    const diff = Math.abs(newBalanceNum - currentBalanceNum);
    
    if (diff >= threshold) {
      console.log('🔄 Balance status changed:', { 
        from: currentBalance, 
        to: newBalance, 
        diff: diff.toFixed(6),
        threshold 
      });
      updateBalance(newBalance);
    } else {
      console.log('⏭️ Balance difference is too small, skipping update:', { 
        from: currentBalance, 
        to: newBalance, 
        diff: diff.toFixed(6),
        threshold 
      });
    }
  }, [formattedBalance, walletBalance, updateBalance]);

  // Listen for connection status changes
  useEffect(() => {
    console.log('🔄 Web3Sync status changed:', { isActive, account: !!account });
  }, [isActive, account]);
};

export default useWeb3Sync;